import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AdminSidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  Clock,
  Settings,
  Plus,
  ChevronLeft,
  ChevronRight,
  Users,
  Home,
  PoundSterling,
  Save,
  RefreshCw,
  Menu,
  X,
  UserPlus,
  Building2,
  Phone,
  Mail,
  CheckCircle,
  AlertCircle,
  Star,
  Trash2,
  Edit
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// UK Counties for coverage
const UK_COUNTIES = [
  // England
  "Greater London", "Greater Manchester", "West Midlands", "West Yorkshire",
  "Merseyside", "South Yorkshire", "Tyne and Wear", "Essex", "Kent", "Hampshire",
  "Lancashire", "Surrey", "Hertfordshire", "Nottinghamshire", "Devon",
  "Staffordshire", "Norfolk", "Leicestershire", "Derbyshire", "Oxfordshire",
  "Suffolk", "Cambridgeshire", "Northamptonshire", "Somerset", "Lincolnshire",
  "Gloucestershire", "Warwickshire", "Dorset", "Worcestershire", "Cornwall",
  "East Sussex", "West Sussex", "Buckinghamshire", "Berkshire", "Bristol",
  // Scotland
  "City of Edinburgh", "Glasgow City", "Aberdeen City", "Dundee City",
  "Fife", "Highland", "South Lanarkshire", "North Lanarkshire",
  // Wales
  "Cardiff", "Swansea", "Newport", "Rhondda Cynon Taf", "Caerphilly",
  // Northern Ireland
  "Belfast", "Antrim and Newtownabbey", "Armagh City, Banbridge and Craigavon"
];

const DAYS_OF_WEEK = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

// Default time windows
const DEFAULT_TIME_WINDOWS = [
  { start: "09:00", end: "12:00" },
  { start: "13:00", end: "17:00" }
];

export default function VaaSAdminCalendar() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  
  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [calendarDateRange, setCalendarDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  
  // County availability state
  const [countyAvailability, setCountyAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [editingCounty, setEditingCounty] = useState(null);
  const [newCountyForm, setNewCountyForm] = useState({
    county: '',
    slotDuration: 20,
    bufferMinutes: 10,
    maxPerDay: 8,
    maxPerWeek: 40,
    days: DAYS_OF_WEEK.map((_, idx) => ({
      dayOfWeek: idx,
      available: idx < 5, // Monday-Friday by default
      timeWindows: [...DEFAULT_TIME_WINDOWS]
    }))
  });
  
  // Pricing state
  const [pricing, setPricing] = useState({
    admin_fee: 50,
    hourly_rate: 35,
    min_charge_minutes: 30,
    travel_charge_per_mile: 0.45,
    weekend_premium_percent: 25,
    portfolio_discount_threshold: 5,
    portfolio_discount_percent: 15
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [newAgentForm, setNewAgentForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    company_registration: '',
    vat_number: '',
    address_line1: '',
    city: '',
    postcode: '',
    counties_covered: [],
    hourly_rate: 25,
    notes: ''
  });

  // Check authentication
  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch pricing
  const fetchPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setPricing(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  }, [token]);

  // Fetch county availability
  const fetchAvailability = useCallback(async () => {
    setAvailabilityLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/availability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCountyAvailability(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [token]);

  // Fetch calendar events
  const fetchCalendarEvents = useCallback(async () => {
    setCalendarLoading(true);
    try {
      let url = `${API_URL}/api/viewings/vaas/calendar?from_date=${calendarDateRange.from}&to_date=${calendarDateRange.to}`;
      if (selectedCounty) {
        url += `&county=${encodeURIComponent(selectedCounty)}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarEvents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [token, selectedCounty, calendarDateRange]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setAgentsLoading(false);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    if (token) {
      fetchPricing();
      fetchAvailability();
      fetchAgents();
    }
  }, [token, fetchPricing, fetchAvailability, fetchAgents]);

  // Fetch calendar when tab changes to calendar
  useEffect(() => {
    if (activeTab === 'calendar' && token) {
      fetchCalendarEvents();
    }
  }, [activeTab, token, fetchCalendarEvents]);

  // Save pricing
  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/pricing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pricing)
      });
      
      if (response.ok) {
        toast.success('VaaS pricing updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update pricing');
      }
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  // Save county availability
  const handleSaveCountyAvailability = async () => {
    if (!newCountyForm.county) {
      toast.error('Please select a county');
      return;
    }
    
    try {
      // Save each day's availability
      for (const day of newCountyForm.days) {
        const response = await fetch(`${API_URL}/api/viewings/vaas/availability`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            county: newCountyForm.county,
            day_of_week: day.dayOfWeek,
            available: day.available,
            time_windows: day.timeWindows,
            max_viewings_per_day: newCountyForm.maxPerDay,
            max_viewings_per_week: newCountyForm.maxPerWeek,
            slot_duration_minutes: newCountyForm.slotDuration,
            buffer_minutes: newCountyForm.bufferMinutes
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save ${DAYS_OF_WEEK[day.dayOfWeek]}`);
        }
      }
      
      toast.success(`Availability saved for ${newCountyForm.county}`);
      fetchAvailability();
      setEditingCounty(null);
      
      // Reset form
      setNewCountyForm({
        county: '',
        slotDuration: 20,
        bufferMinutes: 10,
        maxPerDay: 8,
        maxPerWeek: 40,
        days: DAYS_OF_WEEK.map((_, idx) => ({
          dayOfWeek: idx,
          available: idx < 5,
          timeWindows: [...DEFAULT_TIME_WINDOWS]
        }))
      });
    } catch (error) {
      toast.error(error.message || 'Failed to save availability');
    }
  };

  // Save agent
  const handleSaveAgent = async () => {
    if (!newAgentForm.first_name || !newAgentForm.last_name || !newAgentForm.email) {
      toast.error('Please fill in required fields (First Name, Last Name, Email)');
      return;
    }
    
    try {
      const url = editingAgent 
        ? `${API_URL}/api/viewings/vaas/agents/${editingAgent}`
        : `${API_URL}/api/viewings/vaas/agents`;
      
      const response = await fetch(url, {
        method: editingAgent ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAgentForm)
      });
      
      if (response.ok) {
        toast.success(editingAgent ? 'Agent updated successfully' : 'Agent created successfully');
        fetchAgents();
        setShowAgentForm(false);
        setEditingAgent(null);
        resetAgentForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save agent');
      }
    } catch (error) {
      toast.error('Failed to save agent');
    }
  };

  // Delete agent
  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to deactivate this agent?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Agent deactivated');
        fetchAgents();
      } else {
        toast.error('Failed to deactivate agent');
      }
    } catch (error) {
      toast.error('Failed to deactivate agent');
    }
  };

  // Reset agent form
  const resetAgentForm = () => {
    setNewAgentForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      company_registration: '',
      vat_number: '',
      address_line1: '',
      city: '',
      postcode: '',
      counties_covered: [],
      hourly_rate: 25,
      notes: ''
    });
  };

  // Edit agent
  const handleEditAgent = (agent) => {
    setEditingAgent(agent.id);
    setNewAgentForm({
      first_name: agent.first_name,
      last_name: agent.last_name,
      email: agent.email,
      phone: agent.phone || '',
      company_name: agent.company_name || '',
      company_registration: agent.company_registration || '',
      vat_number: agent.vat_number || '',
      address_line1: agent.address?.line1 || '',
      city: agent.address?.city || '',
      postcode: agent.address?.postcode || '',
      counties_covered: agent.counties_covered || [],
      hourly_rate: agent.hourly_rate || 25,
      notes: agent.notes || ''
    });
    setShowAgentForm(true);
  };

  // Toggle county in agent form
  const toggleAgentCounty = (county) => {
    setNewAgentForm(prev => ({
      ...prev,
      counties_covered: prev.counties_covered.includes(county)
        ? prev.counties_covered.filter(c => c !== county)
        : [...prev.counties_covered, county]
    }));
  };

  // Assign agent to viewing slot
  const handleAssignAgent = async (slotId, agentId) => {
    if (agentId === 'unassigned') {
      // TODO: Implement unassignment if needed
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/viewings/vaas/agents/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slot_id: slotId,
          agent_id: agentId
        })
      });
      
      if (response.ok) {
        toast.success('Agent assigned successfully');
        fetchCalendarEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign agent');
      }
    } catch (error) {
      toast.error('Failed to assign agent');
    }
  };

  // Navigate calendar dates
  const navigateCalendar = (direction) => {
    const days = direction === 'next' ? 14 : -14;
    const from = new Date(calendarDateRange.from);
    const to = new Date(calendarDateRange.to);
    from.setDate(from.getDate() + days);
    to.setDate(to.getDate() + days);
    setCalendarDateRange({
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    });
  };

  // Toggle day availability
  const toggleDayAvailability = (dayIndex) => {
    setNewCountyForm(prev => ({
      ...prev,
      days: prev.days.map((day, idx) =>
        idx === dayIndex ? { ...day, available: !day.available } : day
      )
    }));
  };

  // Get unique dates from calendar events for the calendar view
  const getCalendarDates = () => {
    const dates = [];
    const start = new Date(calendarDateRange.from);
    const end = new Date(calendarDateRange.to);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split('T')[0]);
    }
    return dates;
  };

  // Get events for a specific date
  const getEventsForDate = (dateStr) => {
    return calendarEvents.filter(event => event.date === dateStr);
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="vaas-admin-calendar">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 transform 
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 transition-transform duration-200
      `}>
        <AdminSidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              <h1 className="text-lg font-semibold">VaaS Calendar Management</h1>
            </div>
          </div>
          <div className="text-sm text-slate-500">
            {user?.first_name} {user?.last_name}
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="calendar" className="gap-2">
                <Calendar className="w-4 h-4" />
                Calendar View
              </TabsTrigger>
              <TabsTrigger value="agents" className="gap-2">
                <Users className="w-4 h-4" />
                Agents
              </TabsTrigger>
              <TabsTrigger value="availability" className="gap-2">
                <MapPin className="w-4 h-4" />
                County Availability
              </TabsTrigger>
              <TabsTrigger value="pricing" className="gap-2">
                <PoundSterling className="w-4 h-4" />
                Pricing
              </TabsTrigger>
            </TabsList>

            {/* Calendar Tab */}
            <TabsContent value="calendar">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <CardTitle>VaaS Viewing Schedule</CardTitle>
                      <CardDescription>
                        View and manage all VaaS viewing appointments
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={selectedCounty || 'all'} onValueChange={(value) => setSelectedCounty(value === 'all' ? '' : value)}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="All Counties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Counties</SelectItem>
                          {UK_COUNTIES.map(county => (
                            <SelectItem key={county} value={county}>{county}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fetchCalendarEvents()}
                        disabled={calendarLoading}
                      >
                        <RefreshCw className={`w-4 h-4 ${calendarLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Calendar navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateCalendar('prev')}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm font-medium">
                      {new Date(calendarDateRange.from).toLocaleDateString('en-GB', { 
                        month: 'short', 
                        day: 'numeric' 
                      })} - {new Date(calendarDateRange.to).toLocaleDateString('en-GB', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateCalendar('next')}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {/* Calendar grid */}
                  <div className="overflow-x-auto">
                    <div className="grid grid-cols-7 gap-2 min-w-[700px]">
                      {/* Day headers */}
                      {DAYS_OF_WEEK.map(day => (
                        <div key={day} className="text-center text-xs font-medium text-slate-500 pb-2">
                          {day.slice(0, 3)}
                        </div>
                      ))}
                      
                      {/* Calendar dates */}
                      {getCalendarDates().map(dateStr => {
                        const events = getEventsForDate(dateStr);
                        const date = new Date(dateStr);
                        const isToday = dateStr === new Date().toISOString().split('T')[0];
                        
                        return (
                          <div
                            key={dateStr}
                            className={`
                              min-h-[100px] border rounded-lg p-2
                              ${isToday ? 'bg-primary/5 border-primary' : 'bg-white border-slate-200'}
                            `}
                          >
                            <div className={`
                              text-xs font-medium mb-1
                              ${isToday ? 'text-primary' : 'text-slate-600'}
                            `}>
                              {date.getDate()}
                            </div>
                            <div className="space-y-1">
                              {events.slice(0, 3).map(event => (
                                <div
                                  key={event.id}
                                  className={`
                                    text-xs p-1 rounded truncate cursor-pointer
                                    ${event.status === 'booked' 
                                      ? 'bg-amber-100 text-amber-800' 
                                      : 'bg-emerald-100 text-emerald-800'
                                    }
                                  `}
                                  title={`${formatTime(event.start_time)} - ${event.address}`}
                                >
                                  {formatTime(event.start_time)}
                                </div>
                              ))}
                              {events.length > 3 && (
                                <div className="text-xs text-slate-400 text-center">
                                  +{events.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-emerald-100" />
                      <span>Available</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-amber-100" />
                      <span>Booked</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-blue-100" />
                      <span>Agent Assigned</span>
                    </div>
                  </div>

                  {/* Events list with Agent Assignment */}
                  {calendarEvents.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold mb-3">Upcoming Viewings</h3>
                      <div className="space-y-2">
                        {calendarEvents.slice(0, 10).map(event => (
                          <div
                            key={event.id}
                            className={`
                              flex items-center justify-between p-3 border rounded-lg
                              ${event.assigned_agent_id ? 'bg-blue-50 border-blue-200' : 'bg-white'}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`
                                w-2 h-2 rounded-full
                                ${event.assigned_agent_id ? 'bg-blue-500' : event.status === 'booked' ? 'bg-amber-500' : 'bg-emerald-500'}
                              `} />
                              <div>
                                <p className="text-sm font-medium">{event.address}</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(event.date).toLocaleDateString('en-GB')} at {formatTime(event.start_time)}
                                </p>
                                {event.assigned_agent_name && (
                                  <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                                    <Users className="w-3 h-3" />
                                    Agent: {event.assigned_agent_name}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {event.county && (
                                <Badge variant="outline" className="text-xs">
                                  {event.county}
                                </Badge>
                              )}
                              
                              {/* Agent Assignment Dropdown */}
                              <Select 
                                value={event.assigned_agent_id || 'unassigned'} 
                                onValueChange={(agentId) => handleAssignAgent(event.id, agentId)}
                              >
                                <SelectTrigger className="w-[140px] h-8 text-xs">
                                  <SelectValue placeholder="Assign Agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {agents.filter(a => a.status === 'active').map(agent => (
                                    <SelectItem key={agent.id} value={agent.id}>
                                      {agent.full_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              <Badge className={event.status === 'booked' ? 'bg-amber-500' : 'bg-emerald-500'}>
                                {event.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {calendarEvents.length === 0 && !calendarLoading && (
                    <div className="text-center py-12 text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No VaaS viewings scheduled for this period</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Agents Tab */}
            <TabsContent value="agents">
              <div className="space-y-6">
                {/* Header with Add Button */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">VaaS Agents (Subcontractors)</h2>
                    <p className="text-sm text-slate-500">Manage agents who conduct viewings on your behalf</p>
                  </div>
                  <Button onClick={() => { resetAgentForm(); setEditingAgent(null); setShowAgentForm(true); }}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Agent
                  </Button>
                </div>

                {/* Agent Form */}
                {showAgentForm && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5" />
                        {editingAgent ? 'Edit Agent' : 'Add New Agent'}
                      </CardTitle>
                      <CardDescription>
                        Enter the agent's details. They will conduct viewings in their covered counties.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Personal Details */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>First Name *</Label>
                          <Input
                            value={newAgentForm.first_name}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, first_name: e.target.value }))}
                            placeholder="John"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Name *</Label>
                          <Input
                            value={newAgentForm.last_name}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, last_name: e.target.value }))}
                            placeholder="Smith"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={newAgentForm.email}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="john.smith@example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={newAgentForm.phone}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="07123 456789"
                          />
                        </div>
                      </div>

                      {/* Company Details */}
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Company Name</Label>
                          <Input
                            value={newAgentForm.company_name}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, company_name: e.target.value }))}
                            placeholder="Smith Viewings Ltd"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Company Reg #</Label>
                          <Input
                            value={newAgentForm.company_registration}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, company_registration: e.target.value }))}
                            placeholder="12345678"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>VAT Number</Label>
                          <Input
                            value={newAgentForm.vat_number}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, vat_number: e.target.value }))}
                            placeholder="GB123456789"
                          />
                        </div>
                      </div>

                      {/* Address */}
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input
                            value={newAgentForm.address_line1}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, address_line1: e.target.value }))}
                            placeholder="123 High Street"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input
                            value={newAgentForm.city}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, city: e.target.value }))}
                            placeholder="London"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Postcode</Label>
                          <Input
                            value={newAgentForm.postcode}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder="SW1A 1AA"
                          />
                        </div>
                      </div>

                      {/* Rate */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Hourly Rate (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newAgentForm.hourly_rate}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                          />
                          <p className="text-xs text-slate-500">Rate we pay them per hour</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Notes</Label>
                          <Input
                            value={newAgentForm.notes}
                            onChange={(e) => setNewAgentForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Additional notes..."
                          />
                        </div>
                      </div>

                      {/* Counties Covered */}
                      <div className="space-y-2">
                        <Label>Counties Covered</Label>
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                          {UK_COUNTIES.slice(0, 20).map(county => (
                            <Badge
                              key={county}
                              variant={newAgentForm.counties_covered.includes(county) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => toggleAgentCounty(county)}
                            >
                              {county}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">Click to toggle. Selected: {newAgentForm.counties_covered.length}</p>
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={handleSaveAgent} className="flex-1">
                          <Save className="w-4 h-4 mr-2" />
                          {editingAgent ? 'Update Agent' : 'Create Agent'}
                        </Button>
                        <Button variant="outline" onClick={() => { setShowAgentForm(false); setEditingAgent(null); }}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Agents List */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Registered Agents</CardTitle>
                        <CardDescription>{agents.length} agents registered</CardDescription>
                      </div>
                      <Button variant="outline" size="icon" onClick={fetchAgents} disabled={agentsLoading}>
                        <RefreshCw className={`w-4 h-4 ${agentsLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {agentsLoading ? (
                      <div className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </div>
                    ) : agents.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No agents registered yet</p>
                        <p className="text-sm">Add your first VaaS agent to get started</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {agents.map(agent => (
                          <div
                            key={agent.id}
                            className="p-4 border rounded-lg hover:border-primary/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{agent.full_name}</h4>
                                  {agent.verified ? (
                                    <Badge className="bg-emerald-500 text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Verified
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-amber-600">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Unverified
                                    </Badge>
                                  )}
                                  <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                                    {agent.status}
                                  </Badge>
                                </div>
                                
                                {agent.company_name && (
                                  <div className="flex items-center gap-1 mt-1 text-sm text-slate-600">
                                    <Building2 className="w-3 h-3" />
                                    {agent.company_name}
                                    {agent.company_registration && <span className="text-slate-400">({agent.company_registration})</span>}
                                  </div>
                                )}
                                
                                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {agent.email}
                                  </span>
                                  {agent.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {agent.phone}
                                    </span>
                                  )}
                                  {agent.hourly_rate && (
                                    <span className="flex items-center gap-1">
                                      <PoundSterling className="w-3 h-3" />
                                      £{agent.hourly_rate}/hr
                                    </span>
                                  )}
                                  {agent.performance?.rating && (
                                    <span className="flex items-center gap-1">
                                      <Star className="w-3 h-3 text-amber-500" />
                                      {agent.performance.rating.toFixed(1)}
                                    </span>
                                  )}
                                </div>
                                
                                {agent.counties_covered?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {agent.counties_covered.slice(0, 5).map(county => (
                                      <Badge key={county} variant="outline" className="text-xs">
                                        {county}
                                      </Badge>
                                    ))}
                                    {agent.counties_covered.length > 5 && (
                                      <Badge variant="outline" className="text-xs">
                                        +{agent.counties_covered.length - 5} more
                                      </Badge>
                                    )}
                                  </div>
                                )}

                                {agent.performance && (
                                  <div className="mt-2 text-xs text-slate-500">
                                    {agent.performance.completed_viewings} viewings completed
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditAgent(agent)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteAgent(agent.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* County Availability Tab */}
            <TabsContent value="availability">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Add/Edit County Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      {editingCounty ? 'Edit County Availability' : 'Add County Coverage'}
                    </CardTitle>
                    <CardDescription>
                      Configure when VaaS is available in each county
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>County</Label>
                      <Select 
                        value={newCountyForm.county} 
                        onValueChange={(value) => setNewCountyForm(prev => ({ ...prev, county: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select county" />
                        </SelectTrigger>
                        <SelectContent>
                          {UK_COUNTIES.map(county => (
                            <SelectItem key={county} value={county}>{county}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Slot Duration (mins)</Label>
                        <Select 
                          value={String(newCountyForm.slotDuration)} 
                          onValueChange={(value) => setNewCountyForm(prev => ({ ...prev, slotDuration: parseInt(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="20">20 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Buffer Between (mins)</Label>
                        <Input
                          type="number"
                          value={newCountyForm.bufferMinutes}
                          onChange={(e) => setNewCountyForm(prev => ({ ...prev, bufferMinutes: parseInt(e.target.value) || 0 }))}
                          min={0}
                          max={30}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Max Viewings/Day</Label>
                        <Input
                          type="number"
                          value={newCountyForm.maxPerDay}
                          onChange={(e) => setNewCountyForm(prev => ({ ...prev, maxPerDay: parseInt(e.target.value) || 1 }))}
                          min={1}
                          max={20}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Viewings/Week</Label>
                        <Input
                          type="number"
                          value={newCountyForm.maxPerWeek}
                          onChange={(e) => setNewCountyForm(prev => ({ ...prev, maxPerWeek: parseInt(e.target.value) || 1 }))}
                          min={1}
                          max={100}
                        />
                      </div>
                    </div>

                    {/* Day availability toggles */}
                    <div className="space-y-3">
                      <Label>Available Days</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {DAYS_OF_WEEK.map((day, idx) => (
                          <div
                            key={day}
                            className={`
                              p-2 rounded border cursor-pointer text-center text-sm
                              ${newCountyForm.days[idx].available 
                                ? 'bg-primary/10 border-primary text-primary' 
                                : 'bg-slate-50 border-slate-200 text-slate-400'
                              }
                            `}
                            onClick={() => toggleDayAvailability(idx)}
                          >
                            {day.slice(0, 3)}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Time windows */}
                    <div className="space-y-2">
                      <Label>Default Time Windows</Label>
                      <div className="text-sm text-slate-500">
                        Morning: 09:00 - 12:00 | Afternoon: 13:00 - 17:00
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        onClick={handleSaveCountyAvailability}
                        className="flex-1"
                        disabled={!newCountyForm.county}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Availability
                      </Button>
                      {editingCounty && (
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setEditingCounty(null);
                            setNewCountyForm({
                              county: '',
                              slotDuration: 20,
                              bufferMinutes: 10,
                              maxPerDay: 8,
                              maxPerWeek: 40,
                              days: DAYS_OF_WEEK.map((_, idx) => ({
                                dayOfWeek: idx,
                                available: idx < 5,
                                timeWindows: [...DEFAULT_TIME_WINDOWS]
                              }))
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Existing Counties */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Configured Counties
                    </CardTitle>
                    <CardDescription>
                      {countyAvailability.length} counties with VaaS coverage
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {availabilityLoading ? (
                      <div className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </div>
                    ) : countyAvailability.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No counties configured yet</p>
                        <p className="text-sm">Add your first county to enable VaaS</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {countyAvailability.map(county => (
                          <div
                            key={county.county}
                            className="p-4 border rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                            onClick={() => {
                              setEditingCounty(county.county);
                              setNewCountyForm({
                                county: county.county,
                                slotDuration: county.slot_duration_minutes || 20,
                                bufferMinutes: county.buffer_minutes || 10,
                                maxPerDay: county.max_viewings_per_day || 8,
                                maxPerWeek: county.max_viewings_per_week || 40,
                                days: DAYS_OF_WEEK.map((dayName, idx) => ({
                                  dayOfWeek: idx,
                                  available: county.days?.[dayName]?.available ?? (idx < 5),
                                  timeWindows: county.days?.[dayName]?.time_windows || [...DEFAULT_TIME_WINDOWS]
                                }))
                              });
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium">{county.county}</h4>
                                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                  <Clock className="w-3 h-3" />
                                  {county.slot_duration_minutes || 20} min slots
                                  <span className="mx-1">•</span>
                                  <Users className="w-3 h-3" />
                                  Max {county.max_viewings_per_week || 40}/week
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {DAYS_OF_WEEK.map((day, idx) => (
                                  <div
                                    key={day}
                                    className={`
                                      w-6 h-6 rounded text-xs flex items-center justify-center
                                      ${county.days?.[day]?.available 
                                        ? 'bg-emerald-100 text-emerald-700' 
                                        : 'bg-slate-100 text-slate-400'
                                      }
                                    `}
                                    title={day}
                                  >
                                    {day.charAt(0)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    VaaS Pricing Configuration
                  </CardTitle>
                  <CardDescription>
                    Set pricing for Viewings as a Service
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pricingLoading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Base Fees */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Admin Fee (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={pricing.admin_fee}
                            onChange={(e) => setPricing(prev => ({ ...prev, admin_fee: parseFloat(e.target.value) || 0 }))}
                          />
                          <p className="text-xs text-slate-500">One-time setup fee per property</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Hourly Rate (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={pricing.hourly_rate}
                            onChange={(e) => setPricing(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                          />
                          <p className="text-xs text-slate-500">Rate charged for viewing time</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Min Charge (mins)</Label>
                          <Input
                            type="number"
                            value={pricing.min_charge_minutes}
                            onChange={(e) => setPricing(prev => ({ ...prev, min_charge_minutes: parseInt(e.target.value) || 30 }))}
                          />
                          <p className="text-xs text-slate-500">Minimum billable time</p>
                        </div>
                      </div>

                      {/* Additional Charges */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Travel Charge (£/mile)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={pricing.travel_charge_per_mile}
                            onChange={(e) => setPricing(prev => ({ ...prev, travel_charge_per_mile: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Weekend Premium (%)</Label>
                          <Input
                            type="number"
                            value={pricing.weekend_premium_percent}
                            onChange={(e) => setPricing(prev => ({ ...prev, weekend_premium_percent: parseInt(e.target.value) || 0 }))}
                          />
                        </div>
                      </div>

                      {/* Portfolio Discount */}
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <h4 className="font-medium text-emerald-800 mb-3">Portfolio Discount</h4>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Properties Threshold</Label>
                            <Input
                              type="number"
                              value={pricing.portfolio_discount_threshold}
                              onChange={(e) => setPricing(prev => ({ ...prev, portfolio_discount_threshold: parseInt(e.target.value) || 5 }))}
                            />
                            <p className="text-xs text-slate-500">Min properties to qualify</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Discount (%)</Label>
                            <Input
                              type="number"
                              value={pricing.portfolio_discount_percent}
                              onChange={(e) => setPricing(prev => ({ ...prev, portfolio_discount_percent: parseInt(e.target.value) || 15 }))}
                            />
                            <p className="text-xs text-slate-500">Discount for qualifying landlords</p>
                          </div>
                        </div>
                      </div>

                      {/* Pricing Preview */}
                      <div className="p-4 bg-slate-100 rounded-lg">
                        <h4 className="font-medium mb-2">Example Pricing</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Single Viewing (Weekday)</p>
                            <p className="font-semibold">
                              £{(pricing.admin_fee + (pricing.min_charge_minutes / 60) * pricing.hourly_rate).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500">Single Viewing (Weekend)</p>
                            <p className="font-semibold">
                              £{(pricing.admin_fee + ((pricing.min_charge_minutes / 60) * pricing.hourly_rate) * (1 + pricing.weekend_premium_percent / 100)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button onClick={handleSavePricing} disabled={savingPricing}>
                        {savingPricing ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Pricing
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
